/**
 * AdminLayout — Platform-admin shell for governance UIs (B1.14 / B1.15 / B1.16).
 *
 * Authority: UX Blueprint Faz 0.5 (Governance UI) + ADR-100 §3 (admin API only).
 *
 * Access gate:
 *   - Requires `user.platformRole === 'platform_admin'`.
 *   - Any other role is redirected to `/dashboard`.
 *   - The backend also enforces this via `/api/admin/*` route policy; the
 *     frontend check is UX convenience only (never trust it for security).
 *
 * Visual language:
 *   - Same tablet-first primitives as BusinessLayout (56px touch, no hover-only).
 *   - Top bar carries a distinct "ADMIN" badge so operators never confuse
 *     admin context with regular tenant work.
 */
import {
  BuildingOffice,
  ChartLine,
  FileLock,
  Gear,
  House,
  ShieldStar,
  SignOut,
} from '@phosphor-icons/react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../shared/hooks/useAuth';
import { cn } from '../shared/utils/cn';

interface NavItem {
  to: string;
  labelKey: string;
  icon: ReactNode;
}

const ADMIN_NAV: NavItem[] = [
  {
    to: '/admin/business-profiles',
    labelKey: 'adminNav.profiles',
    icon: <BuildingOffice size={22} weight="regular" />,
  },
  {
    to: '/admin/migrations',
    labelKey: 'adminNav.migrations',
    icon: <ChartLine size={22} weight="regular" />,
  },
  {
    to: '/admin/policies',
    labelKey: 'adminNav.policies',
    icon: <FileLock size={22} weight="regular" />,
  },
];

const FOOTER_NAV: NavItem[] = [
  {
    to: '/dashboard',
    labelKey: 'adminNav.exitAdmin',
    icon: <House size={22} weight="regular" />,
  },
  {
    to: '/settings',
    labelKey: 'adminNav.settings',
    icon: <Gear size={22} weight="regular" />,
  },
];

export function AdminLayout(): JSX.Element {
  const { t } = useTranslation('layout');
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);

  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (user.platformRole !== 'platform_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex h-screen min-h-screen bg-biz-background text-biz-foreground">
      <aside
        className={cn(
          'w-[240px] shrink-0 border-r border-biz-border bg-biz-surface',
          'flex flex-col',
          'hidden lg:flex',
          'z-biz-sidebar',
        )}
      >
        <div className="px-biz-card-lg py-biz-content-lg border-b border-biz-border">
          <div className="flex items-center gap-biz-content-lg">
            <div
              aria-hidden="true"
              className="w-10 h-10 rounded-biz-card bg-biz-accent text-biz-accent-foreground flex items-center justify-center font-bold biz-heading"
            >
              <ShieldStar size={20} weight="fill" />
            </div>
            <div className="flex flex-col">
              <span className="text-biz-base font-semibold biz-heading">Admin</span>
              <span className="text-biz-xs text-biz-muted-foreground uppercase tracking-wider">
                {t('adminNav.platformLabel')}
              </span>
            </div>
          </div>
        </div>

        <nav
          aria-label={t('adminNav.primaryLabel')}
          className="flex-1 overflow-y-auto py-biz-content-lg"
        >
          <ul className="space-y-1 px-biz-content">
            {ADMIN_NAV.map((item) => (
              <SidebarItem
                key={item.to}
                item={item}
                active={isActive(location.pathname, item.to)}
              />
            ))}
          </ul>
        </nav>

        <div className="border-t border-biz-border py-biz-content-lg">
          <ul className="space-y-1 px-biz-content">
            {FOOTER_NAV.map((item) => (
              <SidebarItem
                key={item.to}
                item={item}
                active={isActive(location.pathname, item.to)}
              />
            ))}
          </ul>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header
          className={cn(
            'flex items-center gap-biz-content-lg',
            'px-biz-card-lg border-b border-biz-border',
            'min-h-biz-touch-primary',
            'bg-biz-surface-elevated',
            'z-biz-topbar',
            'biz-safe-area',
          )}
        >
          <span
            className={cn(
              'inline-flex items-center gap-1.5 text-biz-xs font-bold uppercase tracking-wider',
              'px-biz-content py-1 rounded-biz-full border',
              'bg-biz-accent-subtle text-biz-accent border-biz-accent/30',
            )}
          >
            <ShieldStar size={14} weight="fill" aria-hidden="true" />
            {t('adminNav.badge')}
          </span>

          <div className="flex-1" />

          <button
            type="button"
            onClick={handleLogout}
            aria-label={t('topbar.userMenu')}
            className={cn(
              'flex items-center gap-biz-content',
              'min-h-biz-touch px-biz-content-lg rounded-biz-btn',
              'hover:bg-biz-muted',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring',
              'transition-colors duration-biz-fast',
            )}
          >
            <div
              aria-hidden="true"
              className="w-8 h-8 rounded-biz-full bg-biz-accent-subtle text-biz-accent flex items-center justify-center text-biz-sm font-semibold"
            >
              {user.displayName.slice(0, 1).toUpperCase()}
            </div>
            <span className="hidden md:inline text-biz-sm font-medium max-w-[120px] truncate">
              {user.displayName}
            </span>
            <SignOut
              weight="regular"
              size={18}
              aria-hidden="true"
              className="text-biz-muted-foreground"
            />
          </button>
        </header>

        <main
          id="main"
          className="flex-1 overflow-y-auto bg-biz-background biz-safe-area"
          tabIndex={-1}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function SidebarItem({
  item,
  active,
}: {
  item: NavItem;
  active: boolean;
}): JSX.Element {
  const { t } = useTranslation('layout');
  return (
    <li>
      <NavLink
        to={item.to}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'flex items-center gap-biz-content-lg',
          'min-h-biz-touch px-biz-content-lg rounded-biz-btn',
          'text-biz-sm font-medium',
          'transition-colors duration-biz-fast',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring',
          active
            ? 'bg-biz-accent text-biz-accent-foreground'
            : 'text-biz-foreground hover:bg-biz-muted',
        )}
      >
        <span aria-hidden="true" className="shrink-0">
          {item.icon}
        </span>
        <span className="truncate">{t(item.labelKey)}</span>
      </NavLink>
    </li>
  );
}

function isActive(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}
